import type { CountryCode, Region } from "Infrastructure/Api/Api";
import * as countries from "i18n-iso-countries";
import csLocale from "i18n-iso-countries/langs/cs.json";
import deLocale from "i18n-iso-countries/langs/de.json";
import enLocale from "i18n-iso-countries/langs/en.json";
import plLocale from "i18n-iso-countries/langs/pl.json";
import skLocale from "i18n-iso-countries/langs/sk.json";
import viLocale from "i18n-iso-countries/langs/vi.json";
import { useUiLanguage } from "Hooks/useUiLanguage";
import { useCallback, useMemo } from "react";

// Register every app-supported locale once at module load. The API now sends only
// ISO alpha-2 codes (CountryCode / Region); the localized display name lives on the FE.
for (const locale of [
  csLocale,
  deLocale,
  enLocale,
  plLocale,
  skLocale,
  viLocale,
]) {
  countries.registerLocale(locale);
}

/**
 * Resolve a localized country/market name from an ISO alpha-2 code.
 * `Region` values (CZ/SK) are themselves ISO alpha-2 codes, so the same lookup serves both.
 * Falls back to the raw code if no translation is found.
 */
export const getCountryName = (
  code: CountryCode | Region | (string & {}) | null | undefined,
  language: string,
): string => {
  // Guard against non-string input — e.g. a stale redux-persisted codelist whose
  // old shape was an object ({ id, name, alpha2Code }) rather than a plain code.
  // Returning "" keeps callers safe until the codelist refetch replaces the stale state.
  if (!code || typeof code !== "string") {
    return "";
  }

  return countries.getName(code, language) ?? code;
};

/**
 * Memoized country-name resolvers bound to the current UI language: `getCountryName` for one code,
 * `getCountryNames` for a list on one line ("Slovensko, Německo"; "" for none).
 */
export const useCountryNameResolver = () => {
  const language = useUiLanguage();

  const getName = useCallback(
    (code: CountryCode | Region | (string & {}) | null | undefined) =>
      getCountryName(code, language),
    [language],
  );
  const getNames = useCallback(
    (codes: ReadonlyArray<CountryCode>) => codes.map(getName).join(", "),
    [getName],
  );

  return useMemo(
    () => ({ getCountryName: getName, getCountryNames: getNames }),
    [getName, getNames],
  );
};
